import React, { useState } from "react";
import { FiX, FiLock, FiAlertCircle } from "react-icons/fi";

const PaymentModal = ({ isOpen, onClose, onConfirm, amount, isProcessing }) => {
  const [cardData, setCardData] = useState({
    cardholderName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Format card number (add spaces every 4 digits)
    if (name === "cardNumber") {
      const cleanValue = value.replace(/\s/g, "").slice(0, 16);
      const formatted = cleanValue.replace(/(\d{4})/g, "$1 ").trim();
      setCardData((prev) => ({
        ...prev,
        [name]: formatted,
      }));
    } else if (name === "expiryDate") {
      // Format expiry date (MM/YY)
      const cleanValue = value.replace(/\D/g, "").slice(0, 4);
      if (cleanValue.length >= 2) {
        const formatted = cleanValue.slice(0, 2) + "/" + cleanValue.slice(2);
        setCardData((prev) => ({
          ...prev,
          [name]: formatted,
        }));
      } else {
        setCardData((prev) => ({
          ...prev,
          [name]: cleanValue,
        }));
      }
    } else if (name === "cvv") {
      // Only numbers, max 4
      const cleanValue = value.replace(/\D/g, "").slice(0, 4);
      setCardData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else {
      setCardData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!cardData.cardholderName.trim()) {
      newErrors.cardholderName = "El nombre del titular es requerido";
    }

    if (!cardData.cardNumber.replace(/\s/g, "")) {
      newErrors.cardNumber = "El número de tarjeta es requerido";
    } else if (cardData.cardNumber.replace(/\s/g, "").length !== 16) {
      newErrors.cardNumber = "El número debe tener 16 dígitos";
    }

    if (!cardData.expiryDate) {
      newErrors.expiryDate = "La fecha de vencimiento es requerida";
    } else if (!/^\d{2}\/\d{2}$/.test(cardData.expiryDate)) {
      newErrors.expiryDate = "Formato: MM/YY";
    }

    if (!cardData.cvv) {
      newErrors.cvv = "El CVV es requerido";
    } else if (cardData.cvv.length < 3) {
      newErrors.cvv = "El CVV debe tener al menos 3 dígitos";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onConfirm(cardData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal modal-md">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">Detalles de Pago</h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="modal-close"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="form-group">
            {/* Amount */}
            <div className="alert alert-info">
              <div className="alert-content">
                <p className="alert-message alert-message-mb">
                  Monto a pagar
                </p>
                <p className="payment-amount-text">
                  ${amount} USD
                </p>
              </div>
            </div>

            {/* Test Card Info Alert */}
            <div className="alert alert-info alert-mt">
              <FiAlertCircle className="alert-icon" />
              <div className="alert-content">
                <p className="alert-title">Tarjeta de prueba</p>
                <p className="alert-message">
                  Número: 4111 1111 1111 1111<br />
                  Vencimiento: 12/25<br />
                  CVV: 123
                </p>
              </div>
            </div>

            {/* Cardholder Name */}
            <div className="form-group">
              <label className="form-label">Nombre del Titular</label>
              <input
                type="text"
                name="cardholderName"
                value={cardData.cardholderName}
                onChange={handleChange}
                placeholder="JUAN GARCÍA"
                disabled={isProcessing}
                className="form-input"
              />
              {errors.cardholderName && (
                <p className="form-error-text">
                  {errors.cardholderName}
                </p>
              )}
            </div>

            {/* Card Number */}
            <div className="form-group">
              <label className="form-label">Número de Tarjeta</label>
              <input
                type="text"
                name="cardNumber"
                value={cardData.cardNumber}
                onChange={handleChange}
                placeholder="4111 1111 1111 1111"
                disabled={isProcessing}
                maxLength="19"
                className="form-input payment-input-monospace"
              />
              {errors.cardNumber && (
                <p className="form-error-text">
                  {errors.cardNumber}
                </p>
              )}
            </div>

            {/* Expiry and CVV */}
            <div className="payment-grid-2">
              <div className="form-group">
                <label className="form-label">Vencimiento</label>
                <input
                  type="text"
                  name="expiryDate"
                  value={cardData.expiryDate}
                  onChange={handleChange}
                  placeholder="MM/YY"
                  disabled={isProcessing}
                  maxLength="5"
                  className="form-input payment-input-monospace-simple"
                />
                {errors.expiryDate && (
                  <p className="form-error-text">
                    {errors.expiryDate}
                  </p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">CVV</label>
                <input
                  type="text"
                  name="cvv"
                  value={cardData.cvv}
                  onChange={handleChange}
                  placeholder="123"
                  disabled={isProcessing}
                  maxLength="4"
                  className="form-input payment-input-monospace-center"
                />
                {errors.cvv && (
                  <p className="form-error-text">
                    {errors.cvv}
                  </p>
                )}
              </div>
            </div>

            {/* Security Notice */}
            <div className="alert alert-success alert-success-mt">
              <FiLock className="alert-icon" />
              <p className="alert-message">
                Tu información de pago está protegida y cifrada de forma segura
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isProcessing}
            className="btn btn-primary"
          >
            {isProcessing ? (
              <>
                <div className="spinner"></div>
                Procesando...
              </>
            ) : (
              <>
                <FiLock size={18} />
                Pagar Ahora
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
